import { ArrayHelpers, FieldArray, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolButton } from '@synergy/components-next';
import { styled } from '@mui/material';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { DemandAccountFeedbackFormValues } from './feedback-detail-form-modal';
import { DemandAccountItem } from '../../../../models/demand-account-item';
import { DemandSteps } from '../../models/demand-steps';
import { FeedbackCustomerNotes } from './components/feedback-detail-customer-notes';
import { FeedbackDetailDemandItemHeader } from './components/feedback-detail-demand-item-header';
import { FeedbackDetailNewPositionModal } from './components/feedback-detail-new-position-modal';
import { FeedbackDetailTable } from './components/feedback-detail-table';
import { FeedbackInternalNotes } from './components/feedback-detail-internal-notes';
import { demandDetailFeedbackDetailSelector } from '../../store/feedback/demand-detail-feedback-selectors';
import { demandDetailStepperStateSelector } from '../../store/demand-detail-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const StyledPoolButton = styled(PoolButton)(({ theme }) => ({
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),

    '& svg': {
        marginRight: theme.spacing(2),
    },
}));

interface GroupedItems {
    [key: number]: ItemGroup;
}

interface ItemGroup {
    demandItem: DemandAccountItem;
    items: DemandAccountItem[];
}

type FeedbackArrayHelpers = ArrayHelpers & {
    form: FormikProps<DemandAccountFeedbackFormValues>;
    name: string;
};

export const FeedbackDetailForm = () => {
    const demandFeedback = useAppSelector(demandDetailFeedbackDetailSelector);
    const [showAddPositionModal, setShowAddPositionModal] = useState(false);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Feedback);

    const groupByProduct = (demandAccountItems: DemandAccountItem[] = []): ItemGroup[] => {
        const groupedItems: GroupedItems = demandAccountItems.reduce(
            (grouped: GroupedItems, item: DemandAccountItem) => {
                const key = item.demandItemId;

                if (!grouped[key]) {
                    grouped[key] = {
                        demandItem: item,
                        items: [item],
                    };
                } else {
                    grouped[key].items.push(item);
                }
                return grouped;
            },
            {}
        );

        return Object.values(groupedItems);
    };

    const handleAddDemandAccountItem = (helper: FeedbackArrayHelpers) => (demandAccountItem: DemandAccountItem) => {
        helper.push(demandAccountItem);
        handleAddPositionClose();
    };

    const handleAddPositionOpen = () => {
        setShowAddPositionModal(true);
    };

    const handleAddPositionClose = () => {
        setShowAddPositionModal(false);
    };

    const handleQuantityUpdate =
        (form: FormikProps<DemandAccountFeedbackFormValues>, helper: ArrayHelpers) =>
        (demandAccountItem: DemandAccountItem, quantity: number) => {
            const demandAccountItems = form.values.items;
            const index = demandAccountItems.indexOf(demandAccountItem);
            if (index >= 0 && quantity >= 0) {
                const updatedItem = { ...demandAccountItem, quantity };
                helper.replace(index, updatedItem);
            }
        };

    const handleDeleteItem =
        (form: FormikProps<DemandAccountFeedbackFormValues>, helper: ArrayHelpers) =>
        (demandAccountItem: DemandAccountItem) => {
            const demandAccountItems = form.values.items;
            const index = demandAccountItems.indexOf(demandAccountItem);
            if (index >= 0) {
                helper.remove(index);
            }
        };

    return (
        <div>
            {/* TODO: maybe refactor and use a state */}
            <FieldArray name="items">
                {(helper: FeedbackArrayHelpers) => (
                    <div>
                        {groupByProduct(helper.form.values.items).map((item, index) => (
                            <div key={index}>
                                <FeedbackDetailDemandItemHeader demandItem={item.demandItem} />
                                <FeedbackDetailTable
                                    items={item.items}
                                    onQuantityUpdate={handleQuantityUpdate(helper.form, helper)}
                                    onDeleteItem={handleDeleteItem(helper.form, helper)}
                                />
                            </div>
                        ))}
                        {!readonly && (
                            <div>
                                <StyledPoolButton variant="outlined" onClick={handleAddPositionOpen}>
                                    <AddIcon color="primary" />
                                    <FormattedMessage id="app.demands.detail.feedback.showDetails.addPosition" />
                                </StyledPoolButton>
                                {showAddPositionModal && (
                                    <FeedbackDetailNewPositionModal
                                        isVisible={showAddPositionModal}
                                        accountId={demandFeedback?.customer?.id ?? 0}
                                        onAddDemandItem={handleAddDemandAccountItem(helper)}
                                        onClose={handleAddPositionClose}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </FieldArray>
            <FeedbackCustomerNotes />
            <FeedbackInternalNotes />
        </div>
    );
};
